import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RateReviewIcon from '@mui/icons-material/RateReview';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import { auditService, CriterionDetailResponse, TestEvaluation } from '@services/auditService';
import { organizationService } from '@services/organizationService';
import { aiService } from '@services/aiService';
import AttestationPanel from '@components/audit/AttestationPanel';
import AiDraftedNoteLabel from '@components/audit/AiDraftedNoteLabel';
import TestEvaluationVerdictForm from '@components/audit/TestEvaluationVerdictForm';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';
import { conformanceLevelLabel, evidenceFlagLabel } from '@utils/conformanceLabels';

const LEVEL_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  supports: 'success',
  partially_supports: 'warning',
  does_not_support: 'error',
  not_applicable: 'default',
  not_evaluated: 'info',
};

const VERDICT_ICONS: Record<string, JSX.Element> = {
  pass: <CheckCircleIcon color="success" fontSize="small" />,
  fail: <CancelIcon color="error" fontSize="small" />,
  not_applicable: <RemoveCircleOutlineIcon color="disabled" fontSize="small" />,
  not_tested: <RemoveCircleOutlineIcon color="action" fontSize="small" />,
  // REV-1072: amber, from the theme's warning.dark rather than the exact value
  // of chartTokens.verdict.needs_review, and a different GLYPH as well as a
  // different colour — colour alone would not distinguish it for anyone who
  // cannot see the difference (WCAG 1.4.1).
  needs_review: <RateReviewIcon sx={{ color: 'warning.dark' }} fontSize="small" />,
};

export default function CriterionDetailPage() {
  const { slug, auditId, scId } = useParams<{
    slug: string;
    auditId: string;
    scId: string;
  }>();
  const { t } = useTranslation();

  const [criterion, setCriterion] = useState<CriterionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState('');
  // REV-1127: an organization with AI switched off is offered no AI draft.
  const [aiOff, setAiOff] = useState(false);

  // Verdict recording state — REV-359 moved the form to a shared component;
  // the page only tracks which row is currently expanded.
  const [recordingId, setRecordingId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!slug || !auditId || !scId) return;
    setLoading(true);
    setError(null);
    try {
      // REV-802: through the API client, which carries and renews the
      // in-memory token; a raw fetch would do neither. getBySlug unwraps
      // `data.organization` (REV-520).
      const resolvedOrgId = (await organizationService.getBySlug(slug)).id;
      setOrgId(resolvedOrgId);
      // Not awaited with the criterion: an optional AI control must never hold
      // up, or break, the page a reviewer attests on. Unknown means on; the
      // API refuses anyway if it is off.
      void aiService
        .getCredentialStatus(resolvedOrgId)
        .then((status) => setAiOff(status?.aiEnabled === false))
        .catch(() => setAiOff(false));

      const data = await auditService.getCriterion(resolvedOrgId, auditId!, scId!);
      setCriterion(data);
    } catch (err) {
      setError(toDisplayError(err, 'Failed to load criterion'));
    } finally {
      setLoading(false);
    }
  }, [slug, auditId, scId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!criterion) return null;

  const es = criterion.evidence_summary;
  const tes = criterion.test_evaluations || [];
  const tesByCategory = tes.reduce(
    (acc, te) => {
      const cat = te.content_category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(te);
      return acc;
    },
    {} as Record<string, TestEvaluation[]>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Back link */}
      {/* REV-732: goes to a URL, so it is a link. */}
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}/audits/${auditId}`}
        sx={{ mb: 2 }}
      >
        {t('criterion.backToQueue', 'Back to attestation queue')}
      </Button>

      {/* SC header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {t('criterion.heading', '{{sc}} (Level {{level}})', {
            sc: criterion.wcag_sc_id,
            level: criterion.wcag_level,
          })}
        </Typography>

        {/* Suggested + attested levels */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('criterion.suggested', 'Suggested')}
            </Typography>
            <Box>
              <Chip
                label={conformanceLevelLabel(t, criterion.suggested_level)}
                color={LEVEL_COLORS[criterion.suggested_level] || 'default'}
                variant="outlined"
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('criterion.attested', 'Attested')}
            </Typography>
            <Box>
              {criterion.attested_level ? (
                <Chip
                  label={conformanceLevelLabel(t, criterion.attested_level)}
                  color={LEVEL_COLORS[criterion.attested_level] || 'default'}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('criterion.notYetAttested', 'Not yet attested')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Flags */}
        {criterion.suggestion_flags && criterion.suggestion_flags.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {criterion.suggestion_flags.map((flag) => (
              <Chip
                key={flag}
                label={evidenceFlagLabel(t, flag)}
                size="small"
                color={flag === 'critical_path_failure' ? 'error' : 'default'}
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        )}

        {/* Evidence summary */}
        {/* The figures are DATA, not headings. MUI renders variant="h6" as a
            real <h6>, so a count the API did not send produced a heading with
            no accessible name, and six of them nested a heading level under
            the section title for no structural reason (REV-1072 found this
            while adding the needs-review figure). component="p" keeps the
            type scale and drops the bogus heading semantics. */}
        {es && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.pass', 'Pass')}
              </Typography>
              <Typography component="p" variant="h6" color="success.main">
                {es.pass_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.fail', 'Fail')}
              </Typography>
              <Typography component="p" variant="h6" color="error.main">
                {es.fail_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.na', 'N/A')}
              </Typography>
              <Typography component="p" variant="h6">
                {es.na_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.notTested', 'Not Tested')}
              </Typography>
              <Typography component="p" variant="h6" color="text.secondary">
                {es.not_tested_count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.needsReview', 'Needs review')}
              </Typography>
              <Typography component="p" variant="h6" sx={{ color: 'warning.dark' }}>
                {es.needs_review_count ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('criterion.evidence.coverage', 'Coverage')}
              </Typography>
              <Typography component="p" variant="h6">
                {es.coverage_percent}%
              </Typography>
            </Box>
          </Box>
        )}

        {/* Attestation note */}
        {criterion.attestation_note && (
          // REV-704 (same defect, found by reviewing that fix): bgcolor was
          // set and the text colour was left to inherit. `grey.50` is a fixed
          // near-white that does not follow the palette, so in dark mode this
          // put near-white text.primary onto a near-white panel. Theme-aware
          // tokens instead, so the pair moves together in both modes.
          <Box
            sx={{ mt: 2, p: 2, bgcolor: 'action.hover', color: 'text.primary', borderRadius: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('criterion.attestationNote', 'Attestation Note')}
            </Typography>
            <Typography variant="body2">{criterion.attestation_note}</Typography>
            <AiDraftedNoteLabel criterion={criterion} />
          </Box>
        )}
      </Paper>

      {/* Attestation panel */}
      <AttestationPanel
        criterion={criterion}
        onAttest={async ({ level, tier, note, noteAiCallId }) => {
          await auditService.attestCriterion(orgId, auditId!, scId!, {
            level,
            tier,
            note,
            // REV-1074: present only when the note started from an AI draft.
            ...(noteAiCallId ? { noteAiCallId } : {}),
          });
          void fetchData(); // Refresh to show updated attestation state
        }}
        onGenerateNarrative={async () => {
          // REV-356: route through the platform AI proxy. Returns the
          // draft only — saving still goes through onAttest above.
          return auditService.generateNarrative(orgId, auditId!, scId!);
        }}
        aiOff={aiOff}
        disabled={false}
      />

      {/* Test evaluations by category */}
      <Typography variant="h6" gutterBottom>
        {t('criterion.testEvaluations', 'Test Evaluations')} ({tes.length})
      </Typography>

      {Object.entries(tesByCategory)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([category, categoryTes]) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {category} ({categoryTes.length})
            </Typography>
            <ScrollableTableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('criterion.columns.testId', 'Test ID')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('criterion.columns.type', 'Type')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('criterion.columns.verdict', 'Verdict')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('criterion.columns.note', 'Note')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      {t('criterion.columns.actions', 'Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryTes.map((te) => (
                    <TableRow key={te.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {te.test_id}
                        </Typography>
                        {te.is_critical_path && (
                          <Chip label="Critical" size="small" color="error" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={te.check_type.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {VERDICT_ICONS[te.verdict]}
                          <Typography variant="body2">
                            {t(`criterion.verdicts.${te.verdict}`, te.verdict.replace('_', ' '))}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {te.verdict_note || '—'}
                        </Typography>
                        {te.methodology &&
                          Object.values(te.methodology).some(
                            (arr) => Array.isArray(arr) && arr.length > 0
                          ) && (
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {te.methodology.tools?.length ? (
                                <Tooltip
                                  title={te.methodology.tools.join(', ')}
                                  aria-label={t('methodology.tools', 'Tools used')}
                                >
                                  <Chip
                                    size="small"
                                    label={`🛠 ${te.methodology.tools.length}`}
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ) : null}
                              {te.methodology.at_used?.length ? (
                                <Tooltip
                                  title={te.methodology.at_used.join(', ')}
                                  aria-label={t('methodology.atUsed', 'Assistive technology used')}
                                >
                                  <Chip
                                    size="small"
                                    label={`♿ ${te.methodology.at_used.length}`}
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ) : null}
                              {te.methodology.steps_to_reproduce?.length ? (
                                <Tooltip
                                  title={t(
                                    'methodology.stepsCount',
                                    '{{count}} step(s) to reproduce',
                                    { count: te.methodology.steps_to_reproduce.length }
                                  )}
                                >
                                  <Chip
                                    size="small"
                                    label={`${te.methodology.steps_to_reproduce.length} steps`}
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ) : null}
                            </Box>
                          )}
                      </TableCell>
                      <TableCell align="center">
                        {recordingId === te.id ? (
                          <TestEvaluationVerdictForm
                            te={te}
                            orgId={orgId}
                            onSaved={() => {
                              setRecordingId(null);
                              void fetchData();
                            }}
                            onCancel={() => setRecordingId(null)}
                          />
                        ) : (
                          /* describeChild (REV-1072): without it MUI puts the
                             tooltip title on the button as aria-label, so the
                             accessible name became "Record verdict" and the
                             visible label (Evaluate / Review / Update) was not
                             part of it — WCAG 2.5.3 Label in Name — and every
                             row's button announced identically, with nothing
                             to say which state it was for. As a description
                             the title still reaches everyone and the button is
                             named by its own text. */
                          <Tooltip
                            describeChild
                            title={t('criterion.recordVerdict', 'Record verdict')}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setRecordingId(te.id)}
                            >
                              {/* REV-1072: three states, because they ask for
                                  different things. Nothing has looked
                                  (Evaluate); the engine looked and is waiting
                                  on a person (Review); a verdict exists
                                  (Update). */}
                              {te.verdict === 'not_tested'
                                ? t('criterion.evaluate', 'Evaluate')
                                : te.verdict === 'needs_review'
                                  ? t('criterion.review', 'Review')
                                  : t('criterion.update', 'Update')}
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          </Box>
        ))}

      {tes.length === 0 && (
        <Alert severity="info">
          {t('criterion.noTestEvaluations', 'No test evaluations map to this success criterion.')}
        </Alert>
      )}
    </Box>
  );
}
